import type { IntlShape } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLink from 'LuxKit/TextLink'
import CloseButton from 'components/Common/CloseButton/CloseButton'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'

const SnackbarElement = styled.div`
  width: 100%;
  max-width: ${rem(375)};
  padding: ${rem(16)} ${rem(20)};
  border-radius: ${rem(8)};
  box-shadow: ${props => props.theme.shadow.bottom.large};
  background: linear-gradient(96deg, #304dab 14.36%, #4c82df 85.9%);
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
`

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${rem(12)};
  margin-bottom: ${rem(12)};
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const SnackbarCloseButton = styled(CloseButton)`
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
`

const Message = styled(BodyText)`
  color: inherit;
`

const ActionLink = styled(TextLink)`
  color: inherit;
  margin-inline-start: ${rem(4)};
`

interface Props {
  heading: string
  description: string
  action: string
  onClose: () => void
}

function LuxPlusFreePreviewSnackbar({ heading, description, action, onClose }: Props) {
  return <SnackbarElement role="alert">
    <TopRow>
      <LuxPlusLogo colour="inherit" height={17} />
      <SnackbarCloseButton
        horizontalOutdent="end"
        size="small"
        variant="ghost"
        onClick={onClose}
      />
    </TopRow>
    <Content>
      <Heading variant="heading6" colour="static-white">
        {heading}
      </Heading>
      <Message as="p" variant="medium">
        {description}
        <ActionLink
          size="medium"
          underline
          to={`/${LUXURY_PLUS_PATHS.LANDING_PAGE}`}
          onClick={onClose}
        >
          {action}
        </ActionLink>
      </Message>
    </Content>
  </SnackbarElement>
}

export function showLuxPlusFreePreviewSnackbar(intl: IntlShape, firstName?: string) {
  const heading = firstName ?
      intl.formatMessage({
        id: 'B6KTAjZOozFUMdisjg3E',
        defaultMessage: '{firstName}, you’ve unlocked hidden LuxPlus+ offers',
        description: 'Snackbar heading when LuxPlus free preview starts with user name',
      }, { firstName }) :
      intl.formatMessage({
        id: 'AB6KdQzgNntNG3AL16QZ',
        defaultMessage: 'You’ve unlocked hidden LuxPlus+ offers',
        description: 'Snackbar heading when LuxPlus free preview starts without user name',
      })

  const description = intl.formatMessage({
    id: '4aXubAspOXL2LfKGU50a',
    defaultMessage: 'Free Preview gives you access to member-only offers and LuxPlus+ pricing.',
    description: 'Snackbar body message when LuxPlus free preview successfully starts',
  })

  const action = intl.formatMessage({
    id: '9WNghQhttjLlT60pbXcd',
    defaultMessage: 'Learn more',
    description: 'Snackbar action link text for LuxPlus free preview success message',
  })

  showSnackbar(description, 'success', {
    content: ({ onClose }) => <LuxPlusFreePreviewSnackbar
      heading={heading}
      description={description}
      action={action}
      onClose={onClose}
    />,
  })
}

export default LuxPlusFreePreviewSnackbar
